import * as React from 'react';
import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { FavoriteToggle } from '../../common/react/components/FavoriteToggle/FavoriteToggle';
import { DevicePreviewShowPreviewEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { IViewDetailInfo, IViewInfo } from '../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo, IFavoritableItemInfo } from '../../ts/models/resourceInfoParts';
import { DataSources } from '../../ts/resources/Datasources';
import { IWorkbookActions } from '../../ts/ServerApi/types/IWorkbookActions';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { DevicePreviewModalBuilder } from '../content/devicePreview/DevicePreviewModalBuilder';
import { VizViewerWidgetsBar } from '../content/VizViewerWidgetsBar';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';
import { AvailableActions } from '../state/AvailableActionsForEntityState';
import { LayoutType } from '../state/LayoutType';
import { SpaceInfo } from '../state/SpaceInfo';
import { PageHeaderStyles } from './PageHeaderStyles';

export interface VizViewerPageHeaderButtonsProps extends ReactDirectiveProps {
  readonly spaceInfo: SpaceInfo;
  readonly siteUrls: SpacesSiteUrls;
  readonly workbookActions: AvailableActions;
  readonly getWorkbookActions: (workbook: IExplorableItemTypeInfo) => void;
  readonly datasourcesResource: DataSources;
  readonly layoutType: LayoutType;
}

export class VizViewerPageHeaderButtons extends React.PureComponent<VizViewerPageHeaderButtonsProps> {
  public componentDidUpdate() {
    if (isValidSpaceInfoForVizView(this.props.spaceInfo) && !this.props.workbookActions) {
      const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;
      viewInfo.workbook && this.props.getWorkbookActions({ ...viewInfo.workbook, contentType: 'workbook' });
    }
  }

  public render() {
    if (!isValidSpaceInfoForVizView(this.props.spaceInfo)) {
      // don't render this until we get the view info back
      return null;
    }

    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;

    return (
      <div className={PageHeaderStyles.VizViewerButtons}>
        <VizViewerWidgetsBar
          view={viewInfo.workbook ? viewInfo : undefined}
          siteUrls={this.props.siteUrls}
          dataSources={this.props.datasourcesResource}
          showDevicePreview={this.props.workbookActions
            && (this.props.workbookActions as IWorkbookActions).launchWebAuthoring}
          handleDevicePreviewClick={this.handleDevicePreviewClick}
          layoutType={this.props.layoutType}
        />
      </div>);
  }

  private handleDevicePreviewClick = () => {
    Telemetry.sendEvent<DevicePreviewShowPreviewEvent>({
      type: TelemetryEvents.DevicePreviewShowPreview
    });

    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;

    DevicePreviewModalBuilder.openPreview(this.props.angularContext, viewInfo.path);
  }
}


// show a unfavorited button until we get the view info back
const favoriteToggleProps: FavoriteToggle.Props = {
  isFavorite: false,
  onAddFavorite: _.noop,
  onRemoveFavorite: _.noop,
  isTabbable: true,
  buttonType: 'light'
};

/**
 * Viz Viewer Page Favorite Button
 */
export function isValidSpaceInfoForVizView(spaceInfo: SpaceInfo) {
  // First, run.ts changes the NavLocation in the state. That means the NavLocation is 'viz' before
  // we have updated the space info with the correct item. So we need to check that the content type is correct.
  // Second, VizViewCtrl nullifies the space info's item until it gets the view info back, so we need to
  // check that the item is not null before using it.
  return (spaceInfo.item && spaceInfo.item.contentType === 'view');
}

export interface VizViewerFavoriteButtonProps {
  readonly spaceInfo: SpaceInfo;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
}

export class VizViewerFavoriteButton extends React.PureComponent<VizViewerFavoriteButtonProps> {

  public render() {
    if (isValidSpaceInfoForVizView(this.props.spaceInfo)) {
      // show a unfavorited button until we get the view info back
      const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
      favoriteToggleProps.isFavorite = viewInfo.favorite;
      favoriteToggleProps.onAddFavorite = this.onAddFavorite;
      favoriteToggleProps.onRemoveFavorite = this.onRemoveFavorite;
    }

    return (
      <div style={{marginLeft: '-2px', ...CommonStyles.Layout.FlexBlockLeftAndCenter}}>
        <FavoriteToggle {...favoriteToggleProps} />
      </div>
    );
  }

  private onAddFavorite = () => {
    const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
    this.props.addContentFavorite(viewInfo);
  }

  private onRemoveFavorite = () => {
    const viewInfo: IViewInfo = this.props.spaceInfo.item as IViewInfo;
    this.props.removeContentFavorite(viewInfo);
  }
}
